import React, { useState } from 'react';
import { 
  Heart, Menu, X, Users, MessageCircle, BookOpen, HandHeart,
  Baby, Home, Sparkles, HeartHandshake, Stethoscope, Clock, Moon,
  Coffee, Brain, Shield, Droplets, Bath
} from 'lucide-react';
import NavBar from './NavBar'; // Import du composant NavBar
import accompagnementImage1 from './accompagnement-image1.jpg'; // Première image à ajouter
import accompagnementImage2 from './accompagnement-image2.jpg'; // Deuxième image à ajouter
import accompagnementImage3 from './accompagnement-image3.jpg'; // Troisième image à ajouter

const AccompagnementPage = () => {
  const [selectedService, setSelectedService] = useState(null);

  const individualSupport = [
    {
      title: "Soutien post-partum",
      icon: Heart,
      services: [
        "Repos et soulagement du quotidien (soins, écoute, relais émotionnel)",
        "Prévention et accompagnement du baby blues ou du burn-out parental",
        "Valorisation du rôle parental dans un climat de douceur"
      ],
      color: "from-pink-500 to-rose-600"
    },
    {
      title: "Consultation sommeil",
      icon: Moon,
      services: [
        "Accompagnement des troubles du sommeil (endormissement difficile, réveils nocturnes, siestes courtes…)",
        "Outils adaptés et respectueux de la physiologie de votre enfant",
        "Amélioration du sommeil familial sans méthodes brutales"
      ],
      color: "from-blue-500 to-indigo-600"
    },
    {
      title: "Accompagnement",
      icon: Coffee,
      services: [
        "Consultante en lactation certifiée",
        "Départ en allaitement, douleurs, baisse de lactation, sevrage doux",
        "Conseil sur les rythmes, les quantités et les relais biberon"
      ],
      color: "from-amber-500 to-orange-600"
    },
    {
      title: "Guidance parentale",
      icon: Users,
      services: [
        "Difficultés éducatives (pleurs, émotions, crises, opposition)",
        "Accompagnement dans une parentalité consciente, respectueuse et bienveillante",
        "Valorisation des compétences parentales"
      ],
      color: "from-purple-500 to-pink-600"
    },
    {
      title: "Accompagnement émotionnel",
      icon: Brain,
      services: [
        "Espaces de parole confidentiels, sans jugement",
        "Soutien en cas de surcharge mentale, d'anxiété, de culpabilité parentale",
        "Outils pour prendre soin de soi dans son rôle de parent"
      ],
      color: "from-teal-500 to-cyan-600"
    }
  ];

  const workshops = [
    {
      title: "Bain enveloppé",
      icon: Bath,
      description: [
        "Reproduit les sensations in utero pour bébé",
        "Apporte un moment de détente intense et émotionnellement fort",
        "Encadré avec douceur et respect du rythme de l'enfant"
      ]
    },
    {
      title: "Massage bébé",
      icon: HandHeart,
      description: [
        "Favorise la détente, le lien d'attachement et le soulagement des maux (coliques, sommeil, poussées dentaires…)",
        "Atelier guidé pas à pas",
        "Moment de complicité parent-enfant"
      ]
    },
    {
      title: "Atelier portage",
      icon: Baby,
      description: [
        "Choix et utilisation des écharpes ou porte-bébés physiologiques",
        "Respect de la posture et du confort pour le bébé comme le porteur",
        "Encouragement du lien parent-enfant par le contact"
      ]
    },
    {
      title: "Atelier allaitement ou diversification",
      icon: Droplets,
      description: [
        "Informations claires et non jugeantes",
        "Départ en allaitement, reprise du travail, passage au biberon ou diversification alimentaire",
        "Préparation sereine aux transitions alimentaires"
      ]
    }
  ];

  const pillars = [
    {
      title: "Respect du rythme",
      description: "Respect du rythme et des valeurs de chaque parent",
      icon: Clock
    },
    {
      title: "Expertise professionnelle",
      description: "Expertise professionnelle fondée sur l'écoute et la bienveillance",
      icon: Shield
    },
    {
      title: "Outils concrets",
      description: "Transmission d'outils concrets et personnalisés",
      icon: BookOpen
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation avec NavBar */}
      <NavBar />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-rose-500 to-pink-600 overflow-hidden">
        <div className="absolute inset-0">
          <div className="hearts"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center px-4 py-16 md:py-24">
          <Heart className="w-20 h-20 text-pink-100 mx-auto mb-6 animate-pulse" />
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 md:mb-6">
            Accompagnement Parental
          </h1>
          <p className="text-lg md:text-xl text-rose-200">
            Parce que chaque parent mérite d'être accompagné avec respect et douceur
          </p>
        </div>
      </div>

      {/* Introduction avec première image */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="md:flex">
            <div className="md:w-1/2 p-8">
            <h2 className="text-2xl font-bold text-slate-800 mb-8">❤️ Un soutien bienveillant et complet pour votre parentalité</h2>
              <p className="text-slate-600 mb-6 text-lg">
                La parentalité est une aventure aussi belle qu'intense. Entre fatigue, doutes, surcharge mentale et besoin de faire au mieux, il est parfois essentiel de <strong>se faire accompagner par des professionnelles bienveillantes</strong>.
              </p>
              <p className="text-slate-600 mb-6 text-lg">
                Chez <strong>Étoile Veilleuse, Nurse de Jour & de Nuit</strong>, nous vous proposons un <strong>accompagnement parental complet</strong>, à domicile ou en visio, adapté aux besoins des <strong>parents de bébés et jeunes enfants</strong>.
              </p>
              <p className="text-rose-600 font-medium text-lg">
                Écoute, guidance, ateliers pratiques : tout est pensé pour <strong>vous soutenir sans jugement</strong>, avec chaleur, respect et expertise.
              </p>
            </div>
            <div className="md:w-1/2 relative h-64 md:h-auto">
              <img 
                src={accompagnementImage1} 
                alt="Accompagnement parental" 
                className="w-full h-full object-cover absolute inset-0"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Accompagnements individuels */}
      <div className="bg-rose-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-4">
            Nos accompagnements individuels
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-3xl mx-auto text-lg">
            Nous vous proposons des <strong>séances personnalisées</strong> sur les grandes thématiques de la parentalité :
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {individualSupport.map((support, index) => (
              <div 
                key={index} 
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => setSelectedService(selectedService === index ? null : index)}
              >
                <div className={`bg-gradient-to-r ${support.color} p-6 text-white`}>
                  <support.icon className="w-12 h-12 mb-4" />
                  <h3 className="text-xl font-bold">{support.title}</h3>
                </div>
                <div className={`p-6 transition-all duration-300 ${selectedService === index ? 'max-h-96' : 'max-h-0 overflow-hidden'}`}>
                  <ul className="space-y-3">
                    {support.services.map((service, idx) => (
                      <li key={idx} className="flex items-start">
                        <Sparkles className="w-5 h-5 text-rose-400 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600 text-sm">{service}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ateliers parent-bébé avec deuxième image */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-4">
          Nos ateliers parent-bébé
        </h2>
        <p className="text-center text-slate-600 mb-12 max-w-3xl mx-auto text-lg">
          Nous organisons également des <strong>ateliers à domicile ou en petit groupe</strong>, pour vous reconnecter à votre bébé, à vos émotions et à vos compétences parentales :
        </p>
        
        <div className="grid md:grid-cols-2 gap-8 items-center mb-12">
          <div>
            <img 
              src={accompagnementImage2} 
              alt="Ateliers parent-bébé" 
              className="w-full h-full object-cover rounded-lg shadow-lg"
              style={{ maxHeight: '400px' }}
            />
          </div>
          
          <div className="grid gap-6">
            {workshops.slice(0, 2).map((workshop, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center mb-4">
                  <workshop.icon className="w-10 h-10 text-rose-500 mr-4" />
                  <h3 className="text-xl font-bold text-slate-800">{workshop.title}</h3>
                </div>
                <ul className="space-y-2">
                  {workshop.description.map((desc, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-rose-400 mr-2">•</span>
                      <span className="text-slate-600 text-sm">{desc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {workshops.slice(2).map((workshop, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <workshop.icon className="w-10 h-10 text-rose-500 mr-4" />
                <h3 className="text-xl font-bold text-slate-800">{workshop.title}</h3>
              </div>
              <ul className="space-y-2">
                {workshop.description.map((desc, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-rose-400 mr-2">•</span>
                    <span className="text-slate-600 text-sm">{desc}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Approche professionnelle avec troisième image */}
      <div className="bg-gradient-to-br from-rose-100 to-pink-100 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-8">
            Une approche humaine, respectueuse et professionnelle
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-slate-600 mb-6 text-lg">
                Tous nos accompagnements sont assurés par des professionnelles de santé et de la petite enfance : <strong>infirmières, auxiliaires de puériculture, accompagnantes parentales, consultantes en lactation ou périnatalité</strong>, formées à l'écoute active, au soutien émotionnel et aux besoins spécifiques de chaque famille.
              </p>
              
              <h3 className="text-xl font-bold text-slate-800 mb-6">Notre approche repose sur trois piliers :</h3>
              
              <div className="space-y-4">
                {pillars.map((pillar, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <pillar.icon className="w-8 h-8 text-rose-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-slate-800">{pillar.title}</h4>
                      <p className="text-slate-600">{pillar.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <img 
                src={accompagnementImage3} 
                alt="Approche professionnelle" 
                className="w-full h-full object-cover rounded-lg shadow-lg"
                style={{ maxHeight: '400px' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-r from-rose-600 to-pink-700 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Prêts à Commencer Cette Belle Aventure Ensemble ?
          </h2>
          <p className="text-xl mb-8 text-rose-100">
            Contactez-nous pour discuter de vos besoins et créer un programme sur mesure
          </p>
          <a 
            href="mailto:etoileveilleuse@gmail.com"
            className="inline-block bg-white text-rose-600 px-8 py-4 rounded-full font-semibold hover:bg-rose-50 transform transition hover:scale-105"
          >
            Demander un Premier Rendez-vous
          </a>
        </div>
      </div>

      {/* Styles pour les cœurs animés */}
      <style jsx>{`
        .hearts {
          position: absolute;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }
        
        .hearts::before,
        .hearts::after {
          content: '♥';
          position: absolute;
          color: rgba(255, 255, 255, 0.2);
          font-size: 40px;
          animation: float-heart 15s infinite linear;
        }
        
        .hearts::before {
          left: 10%;
          animation-delay: 0s;
        }
        
        .hearts::after {
          left: 80%;
          animation-delay: 5s;
        }
        
        @keyframes float-heart {
          0% {
            transform: translateY(100vh) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 0.3;
          }
          90% {
            opacity: 0.3;
          }
          100% {
            transform: translateY(-100vh) rotate(360deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default AccompagnementPage;